import { useQuery, useQueryClient } from "@tanstack/react-query"

import { MessageChannel } from "@bullseye/types"

import { useHTTPContext } from "../providers/HTTPProvider"
import { useUserContext } from "../providers"

export const useMessageChannelByStreamId = (id: string | undefined | null) => {
  const { accessToken } = useUserContext()
  const { apiBase } = useHTTPContext()
  const queryClient = useQueryClient()

  const getCachedData = (): MessageChannel | undefined => {
    return queryClient.getQueryData<MessageChannel>([
      "message-channel-by-stream-id",
      id,
    ])
  }

  const { data, isLoading, error, refetch, isRefetching } = useQuery(
    ["message-channel-by-stream-id", id],
    async () => {
      const res = await fetch(`${apiBase}/v1/message-channels/${id}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      })

      if (!res.ok) {
        throw new Error("Failed to fetch message channel")
      }

      const json = (await res.json()) as { data: MessageChannel[] }
      return json.data?.[0]
    },
    {
      enabled: !!id && !!accessToken,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
      retry: false,
      initialData: () => getCachedData(),
      initialDataUpdatedAt: () =>
        getCachedData() ? Date.now() - 1000 : undefined,
    },
  )

  return {
    messageChannel: data,
    isLoading,
    error,
    refetch,
    isRefetching,
    key: ["message-channel-by-stream-id", id],
  }
}